export const MIN_STREAM_GRAPH_HEIGHT_PX = 12

export const MIN_STREAM_APP_HEIGHT_PX = 4

export const STREAM_GRAPH_SPACING_TOP_PX = 10
export const STREAM_GRAPH_SPACING_BOTTOM_PX = 20

const pointValue = (y: number | undefined): number =>
  typeof y === 'number' && Number.isFinite(y) && y > 0 ? y : 0

type StreamPoint = {y: number; dataConsumed: number}

type StreamSeries<T extends StreamPoint> = {data: readonly T[]}

/**
 * Plot height is the chart container minus the stream chart's vertical spacing.
 * spacingTop matches Highcharts' default so the graph does not move.
 */
export const getStreamGraphPlotHeight = (containerHeight: number): number =>
  Math.max(
    containerHeight -
      STREAM_GRAPH_SPACING_TOP_PX -
      STREAM_GRAPH_SPACING_BOTTOM_PX,
    0,
  )

/**
 * Raise a day's stacked total to at least 12px, then raise each app with
 * traffic to at least 4px. The peak day is the scale, so a quiet day is capped
 * back to that total and cannot undo the floor. `y` is the drawn value;
 * `dataConsumed` stays the real byte total for the tooltip.
 */
export const applyStreamGraphMinHeight = <T extends StreamPoint>(
  series: readonly StreamSeries<T>[],
  plotHeight: number,
  minHeightPx = MIN_STREAM_GRAPH_HEIGHT_PX,
  minAppHeightPx = MIN_STREAM_APP_HEIGHT_PX,
): {data: T[]}[] => {
  const dayCount = series[0]?.data.length ?? 0
  const dayTotals = Array.from({length: dayCount}, (_, day) =>
    series.reduce((sum, item) => sum + pointValue(item.data[day]?.y), 0),
  )
  const maxTotal = Math.max(0, ...dayTotals)
  if (plotHeight <= minAppHeightPx || maxTotal === 0) {
    return series as {data: T[]}[]
  }

  const minDayTotal =
    plotHeight > minHeightPx ? (minHeightPx / plotHeight) * maxTotal : 0
  const minAppTotal = (minAppHeightPx / plotHeight) * maxTotal
  const drawnByDay = Array.from({length: dayCount}, (_, day) => {
    const dayTotal = dayTotals[day] ?? 0
    const drawn = series.map(item => {
      const original = pointValue(item.data[day]?.y)
      const afterDayFloor =
        dayTotal > 0 && dayTotal < minDayTotal
          ? (original / dayTotal) * minDayTotal
          : original
      return afterDayFloor > 0 ? Math.max(afterDayFloor, minAppTotal) : 0
    })
    const drawnSum = drawn.reduce((sum, value) => sum + value, 0)
    if (drawnSum <= maxTotal) {
      return drawn
    }
    const scale = maxTotal / drawnSum
    return drawn.map(value => value * scale)
  })
  const unchanged = drawnByDay.every((dayValues, day) =>
    dayValues.every(
      (value, seriesIndex) =>
        value === pointValue(series[seriesIndex]?.data[day]?.y),
    ),
  )
  if (unchanged) {
    return series as {data: T[]}[]
  }

  return series.map((item, seriesIndex) => ({
    ...item,
    data: item.data.map((point, day) => ({
      ...point,
      y: drawnByDay[day]?.[seriesIndex] ?? pointValue(point.y),
    })),
  }))
}
